import React from 'react';
import axios from 'axios';
import { EmailNotValidatedError } from './errors';
import { AUTH_CONSTANTS } from './auth-constants';
import { EmailNotValidatedModal } from '../../presenters/components/email-not-validated-modal';
import { analytics } from '../services/analytics';

/**
 * Interface para os dados extraídos do erro de email não validado
 */
interface EmailNotValidatedErrorData {
    message: string;
    instructions: string[];
    supportMessage?: string;
    canResendEmail: boolean;
}

/**
 * Interface para o resultado do handler
 */
export interface EmailNotValidatedHandlerResult {
    isEmailNotValidated: boolean;
    errorTitle: string;
    errorTextComponent: (() => JSX.Element) | null;
}

/**
 * Interface para as opções do handler
 */
interface EmailNotValidatedHandlerOptions {
    error: any;
    resendingEmail: boolean;
    onResend: () => void;
    defaultMessage?: string;
    context?: string; // Para analytics/logging (ex: 'login', 'forgot_password', 'reset_password')
    allowedStatusCodes?: number[]; // Status codes permitidos para detecção (padrão: [401, 403])
}

/**
 * Detecta se um erro é de email não validado e retorna os componentes necessários para exibição
 * 
 * Segue princípios DRY e SOLID:
 * - Single Responsibility: apenas detecta e formata erro de email não validado
 * - Reutilizável: pode ser usado em login, forgot-password, reset-password
 * - Centraliza a lógica de detecção e formatação
 * 
 * @param options - Opções do handler
 * @returns Objeto com informações sobre se é erro de email não validado e componentes para exibição
 */
export const handleEmailNotValidatedError = (
    options: EmailNotValidatedHandlerOptions
): EmailNotValidatedHandlerResult => {
    const {
        error,
        resendingEmail,
        onResend,
        defaultMessage = 'Por favor, confirme seu email antes de continuar.',
        context = 'unknown',
        allowedStatusCodes = [401, 403]
    } = options;

    // Detectar se é erro de email não validado
    const responseStatus = error?.response?.status;
    const responseError = error?.response?.data?.error;
    
    const isEmailNotValidated = 
        error instanceof EmailNotValidatedError ||
        error?.name === 'EmailNotValidatedError' ||
        error?.constructor?.name === 'EmailNotValidatedError' ||
        (allowedStatusCodes.includes(responseStatus || 0) && 
         responseError === AUTH_CONSTANTS.ERRORS.EMAIL_NOT_VALIDATED);

    // Log para analytics
    if (isEmailNotValidated) {
        analytics.addBreadcrumb(
            `Email not validated error detected in ${context} - status: ${responseStatus}, error: ${responseError}`,
            'auth',
            'info'
        );
    }

    // Se não for erro de email não validado, retornar resultado vazio
    if (!isEmailNotValidated) {
        return {
            isEmailNotValidated: false,
            errorTitle: '',
            errorTextComponent: null
        };
    }

    // Extrair dados do erro
    let errorData: EmailNotValidatedErrorData;
    
    if (error instanceof EmailNotValidatedError) {
        // Se for instância de EmailNotValidatedError, usar diretamente
        errorData = {
            message: error.message,
            instructions: error.instructions || [],
            supportMessage: error.supportMessage,
            canResendEmail: error.canResendEmail !== false,
        };
    } else {
        // Extrair do response do axios
        const responseData = error?.response?.data || {};
        errorData = {
            message: responseData.message || error?.message || defaultMessage,
            instructions: responseData.instructions || error?.instructions || [],
            supportMessage: responseData.support_message || error?.supportMessage,
            canResendEmail: responseData.can_resend_email !== false && error?.canResendEmail !== false,
        };
    }

    // Criar componente do modal
    const errorTextComponent = () => (
        <EmailNotValidatedModal
            message={errorData.message}
            instructions={errorData.instructions}
            supportMessage={errorData.supportMessage}
            canResendEmail={errorData.canResendEmail}
            resendingEmail={resendingEmail}
            onResend={onResend}
        />
    );

    return {
        isEmailNotValidated: true,
        errorTitle: 'Email não confirmado',
        errorTextComponent
    };
};

